/**
 * OrchestratorContext — provides the T6 orchestrators to the React tree.
 *
 * Orchestrators are constructed once at app entry (see `main.tsx`) with the
 * singleton adapters + root store. Feature screens pull them via
 * `useOrchestrators()` rather than importing module-level singletons from
 * `main.tsx`; that keeps feature modules testable (each test can mount its
 * own provider with spy adapters) and avoids a circular import between
 * `features/*` and `main.tsx`.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { ShoppingListsOrchestrator } from './shopping-lists-orchestrator';
import type { HouseholdProductsOrchestrator } from './HouseholdProductsOrchestrator';
import type { RetailerOffersOrchestrator } from './RetailerOffersOrchestrator';

export interface OrchestratorBundle {
  ShoppingLists: ShoppingListsOrchestrator;
  HouseholdProducts: HouseholdProductsOrchestrator;
  RetailerOffers: RetailerOffersOrchestrator;
}

const OrchestratorContext = createContext<OrchestratorBundle | null>(null);

export interface OrchestratorProviderProps {
  orchestrators: OrchestratorBundle;
  children: ReactNode;
}

export function OrchestratorProvider({
  orchestrators,
  children,
}: OrchestratorProviderProps): JSX.Element {
  return (
    <OrchestratorContext.Provider value={orchestrators}>
      {children}
    </OrchestratorContext.Provider>
  );
}

export function useOrchestrators(): OrchestratorBundle {
  const ctx = useContext(OrchestratorContext);
  if (!ctx) {
    throw new Error(
      'useOrchestrators must be used inside <OrchestratorProvider>. ' +
        'Check that your test mounts one, and that main.tsx wraps the app.'
    );
  }
  return ctx;
}
